import { useEffect, useMemo, useState, type CSSProperties } from "react";
import { showToast } from "../primitives";
import { useChildNodes } from "../../hooks/use-child-nodes";
import { useSupersedeMemory } from "../../hooks/use-memory-history";
import type { LibraryNode } from "../../../lib/types";

/**
 * Sprint 20 — owner-only fact correction. Supersede an outdated memory by a
 * newer one (or retire it with no successor). Candidate replacements are the
 * sibling memories under the same agent, minus this memory and ones already
 * superseded. The old memory stays visible, marked superseded, unless "archive"
 * is ticked.
 */
interface SupersedeMemoryModalProps {
  open: boolean;
  onClose: () => void;
  memory: LibraryNode;
  onSuperseded?: () => void;
}

export function SupersedeMemoryModal({ open, onClose, memory, onSuperseded }: SupersedeMemoryModalProps) {
  const siblings = useChildNodes(memory.parent_id ?? undefined, "memory", false);
  const supersede = useSupersedeMemory();

  const [search, setSearch] = useState("");
  const [replacementId, setReplacementId] = useState<string | null>(null);
  const [reason, setReason] = useState("");
  const [archiveOld, setArchiveOld] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setSearch("");
      setReplacementId(null);
      setReason("");
      setArchiveOld(false);
      setBusy(false);
    }
  }, [open]);

  const candidates = useMemo<LibraryNode[]>(() => {
    const all = (siblings.data ?? []).filter(
      (m) => m.id !== memory.id && m.status !== "superseded",
    );
    const q = search.trim().toLowerCase();
    return q ? all.filter((m) => m.name.toLowerCase().includes(q)) : all;
  }, [siblings.data, memory.id, search]);

  if (!open) return null;

  async function handleConfirm() {
    setBusy(true);
    try {
      await supersede.mutateAsync({
        memory_id: memory.id,
        ...(replacementId ? { superseded_by_memory_id: replacementId } : {}),
        ...(reason.trim() ? { reason: reason.trim() } : {}),
        ...(archiveOld ? { archive_old: true } : {}),
      });
      showToast(`Superseded '${memory.name}'`, "success");
      onSuperseded?.();
      onClose();
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Supersede failed", "error", true);
      setBusy(false);
    }
  }

  return (
    <div style={overlay} onClick={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div style={modal} role="dialog" aria-label="Supersede memory">
        <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 4, color: "var(--color-text-primary)" }}>
          Supersede “{memory.name}”
        </div>
        <div style={{ fontSize: 12, color: "var(--color-text-tertiary)", marginBottom: 16 }}>
          Mark this fact outdated and (optionally) point it at the memory that replaced it. It stays
          visible for audit but drops out of default recall.
        </div>

        <input
          type="text"
          autoFocus
          style={input}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search replacement memories…"
        />

        <div style={{ overflowY: "auto", flex: 1, minHeight: 0, margin: "0 -4px" }}>
          <button
            type="button"
            onClick={() => setReplacementId(null)}
            style={candidateStyle(replacementId === null)}
          >
            <i className="ti ti-circle-off" style={candidateIcon} aria-hidden="true" />
            <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)" }}>
              No successor (retire this fact)
            </span>
          </button>
          {siblings.isLoading ? (
            <div style={hintStyle}>Loading memories…</div>
          ) : candidates.length === 0 ? (
            <div style={hintStyle}>No other memories under this agent.</div>
          ) : (
            candidates.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => setReplacementId(m.id)}
                style={candidateStyle(m.id === replacementId)}
              >
                <i className="ti ti-bulb" style={candidateIcon} aria-hidden="true" />
                <span style={{ fontSize: 13, fontWeight: 500, color: "var(--color-text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {m.name}
                </span>
              </button>
            ))
          )}
        </div>

        <input
          type="text"
          style={{ ...input, marginTop: 12, marginBottom: 10 }}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional, kept in the audit trail)"
        />

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--color-text-secondary)", cursor: "pointer" }}>
          <input type="checkbox" checked={archiveOld} onChange={(e) => setArchiveOld(e.target.checked)} />
          Also archive the old memory (hide it from the tree)
        </label>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
          <button type="button" onClick={onClose} disabled={busy} style={{ fontSize: 13, padding: "8px 14px" }}>
            Cancel
          </button>
          <button
            type="button"
            data-variant="primary"
            onClick={handleConfirm}
            disabled={busy}
            style={{ fontSize: 13, padding: "8px 14px" }}
          >
            {busy ? "Superseding…" : replacementId ? "Supersede" : "Retire"}
          </button>
        </div>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.3)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 100,
  fontFamily: "var(--font-sans)",
};

const modal: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-lg)",
  padding: 24,
  width: 460,
  maxWidth: "90vw",
  display: "flex",
  flexDirection: "column",
  maxHeight: "80vh",
};

const input: CSSProperties = {
  width: "100%",
  fontSize: 13,
  padding: "8px 10px",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  background: "var(--color-background-primary)",
  color: "var(--color-text-primary)",
  fontFamily: "var(--font-sans)",
  marginBottom: 12,
  boxSizing: "border-box",
};

const hintStyle: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
  padding: "12px 4px",
};

const candidateIcon: CSSProperties = {
  fontSize: 15,
  color: "var(--color-text-secondary)",
  flexShrink: 0,
};

function candidateStyle(selected: boolean): CSSProperties {
  return {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    textAlign: "left",
    padding: "8px",
    border: "none",
    borderRadius: "var(--border-radius-sm)",
    background: selected ? "var(--color-background-info-subtle, var(--color-background-secondary))" : "transparent",
    cursor: "pointer",
    fontFamily: "var(--font-sans)",
    boxShadow: selected ? "inset 0 0 0 1px var(--color-text-info)" : "none",
  };
}
